import { TICKETING_FORM_KEY } from './ticketingFormKeys';

const HR_RESOURCE_LABELS = {
  email: 'Email',
  windows_account: 'Windows account',
  erp: 'ERP',
  wms: 'WMS',
  pc_laptop: 'PC / Laptop',
  mobile: 'Mobile',
  ip_phone: 'IP Phone',
  access_card: 'Access card',
  rf_scanner: 'RF (scanner)',
};

const WAREHOUSE_KIND_LABELS = {
  stock: 'Stock / inventory',
  equipment: 'Equipment',
  access: 'Access / badges',
};

const URGENCY_LABELS = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  critical: 'Critical',
  urgent: 'Urgent',
};

const IMPACT_LABELS = {
  all_company: 'All company',
  my_department: 'My department',
  only_me: 'Only me',
};

const IT_CATEGORY_LABELS = {
  hardware: 'Hardware',
  software: 'Software',
  other: 'Other',
};

function humanizeKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function clip(text, maxLen) {
  const s = text == null ? '' : String(text).trim();
  if (!s) return '';
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1)).trimEnd()}…`;
}

/**
 * Human-readable lines for list tooltips and previews (aligned with TicketingPayloadViewer).
 * @returns {{ full: string, preview: string }}
 */
export function getTicketingPayloadSummary(formKey, payload, previewMaxLen = 180) {
  const p = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  const parts = [];
  const subjectText = p.subject && String(p.subject).trim() ? String(p.subject).trim() : '';
  if (subjectText) {
    parts.push(`Subject: ${subjectText}`);
  }

  switch (formKey) {
    case TICKETING_FORM_KEY.HR_EMPLOYEE_IT:
    case TICKETING_FORM_KEY.HR_EMPLOYEE_EDIT_IT:
    case TICKETING_FORM_KEY.HR_EMPLOYEE_DELETE_IT: {
      if (p.userDepartmentName && String(p.userDepartmentName).trim()) {
        parts.push(`Dept: ${String(p.userDepartmentName).trim()}`);
      } else if (p.userDepartmentId != null && String(p.userDepartmentId).trim() !== '') {
        parts.push(`Dept ID: ${String(p.userDepartmentId).trim()}`);
      }
      if (p.employeeFullName && String(p.employeeFullName).trim()) {
        parts.push(`Name: ${String(p.employeeFullName).trim()}`);
      }
      const dateLabel =
        formKey === TICKETING_FORM_KEY.HR_EMPLOYEE_EDIT_IT
          ? 'Change'
          : formKey === TICKETING_FORM_KEY.HR_EMPLOYEE_DELETE_IT
            ? 'Withdrawal'
            : 'Recruitment';
      const dateVal =
        p.recruitmentDate || p.changeDate || p.withdrawalDate;
      if (dateVal && String(dateVal).trim()) {
        parts.push(`${dateLabel}: ${String(dateVal).trim()}`);
      }
      if (p.workingHours && String(p.workingHours).trim()) {
        parts.push(`Hours: ${String(p.workingHours).trim()}`);
      }
      const resources = Object.entries(p.itResources || {})
        .filter(([, on]) => Boolean(on))
        .map(([id]) => HR_RESOURCE_LABELS[id] || humanizeKey(id));
      if (resources.length) parts.push(`Resources: ${resources.join(', ')}`);
      if (Array.isArray(p.warehouse)) {
        const sites = p.warehouse
          .filter((v) => v != null && String(v).trim() !== '')
          .map((v) => String(v).trim());
        if (sites.length) {
          parts.push(`Sites: ${sites.join(', ')}`);
        }
      } else if (p.warehouse) {
        parts.push(`Site ${p.warehouse}`);
      }
      if (p.comments && String(p.comments).trim()) parts.push(String(p.comments).trim());
      break;
    }
    case TICKETING_FORM_KEY.WAREHOUSE_ACCESS: {
      if (p.locationZone && String(p.locationZone).trim()) {
        parts.push(`Location: ${String(p.locationZone).trim()}`);
      }
      if (p.requestKind != null) {
        parts.push(
          `Type: ${WAREHOUSE_KIND_LABELS[p.requestKind] || humanizeKey(p.requestKind)}`,
        );
      }
      if (p.urgency != null) {
        parts.push(`Urgency: ${URGENCY_LABELS[p.urgency] || humanizeKey(p.urgency)}`);
      }
      if (p.comments && String(p.comments).trim()) parts.push(String(p.comments).trim());
      break;
    }
    case TICKETING_FORM_KEY.GENERAL: {
      if (p.description && String(p.description).trim()) {
        parts.push(String(p.description).trim());
      }
      if (p.urgency != null) {
        parts.push(`Urgency: ${URGENCY_LABELS[p.urgency] || humanizeKey(p.urgency)}`);
      }
      if (p.impact != null) {
        parts.push(`Impact: ${IMPACT_LABELS[p.impact] || humanizeKey(p.impact)}`);
      }
      break;
    }
    case TICKETING_FORM_KEY.GENERAL_IT: {
      if (p.description && String(p.description).trim()) {
        parts.push(String(p.description).trim());
      }
      if (p.itCategory != null) {
        parts.push(
          `Category: ${IT_CATEGORY_LABELS[p.itCategory] || humanizeKey(p.itCategory)}`,
        );
      }
      if (p.urgency != null) {
        parts.push(`Urgency: ${URGENCY_LABELS[p.urgency] || humanizeKey(p.urgency)}`);
      }
      if (p.impact != null) {
        parts.push(`Impact: ${IMPACT_LABELS[p.impact] || humanizeKey(p.impact)}`);
      }
      break;
    }
    default: {
      const entries = Object.entries(p);
      for (const [k, v] of entries) {
        if (v === null || v === undefined) continue;
        if (typeof v === 'object' && !Array.isArray(v)) {
          parts.push(`${humanizeKey(k)}: {…}`);
        } else if (Array.isArray(v)) {
          parts.push(`${humanizeKey(k)}: [${v.length}]`);
        } else {
          const str = String(v).trim();
          if (str) parts.push(`${humanizeKey(k)}: ${str}`);
        }
      }
      if (!parts.length) {
        try {
          const raw = JSON.stringify(p);
          parts.push(raw === '{}' ? '' : clip(raw, 400));
        } catch {
          parts.push('');
        }
      }
    }
  }

  const full = parts.filter(Boolean).join(' · ') || 'No request details';
  const preview = clip(full, previewMaxLen) || 'No request details';
  return { full, preview };
}
